import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { assetTypeValues } from "../../../constants";

export const CURRENCY_EXPOSURE_BREAKDOWN_COLUMNS = [
  { key: "lease_ref_code", label: "Lease Ref Code" },
  { key: "lessee", label: "Lessee" },
  { key: "asset_label", label: "Asset" },
  { key: "invoiced_amount", label: "Invoiced Amount", mono: true },
];

// Amounts are native to each row's currency (no FX conversion), so format with that currency.
function formatAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency || ""} ${amount.toFixed(2)}`.trim();
  }
}

function buildCurrencyExposureRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: `${item?.currency || "ccy"}-${item?.lease_ref_code || "lease"}-${index}`,
    currency: item?.currency || "Unknown",
    lease_ref_code: item?.lease_ref_code || "-",
    lessee: item?.lessee || "-",
    asset_label: [assetTypeValues[item?.asset_type]?.label, item?.asset_sn].filter(Boolean).join(" ") || "-",
    invoiced_amount: formatAmount(item?.invoiced_amount_native, item?.currency),
  }));
}

// Clicking a pie slice opens the popup with currency_exposure_breakdown filtered to that currency.
export function useCurrencyExposureBreakdown({ currencyBreakdown, onTrack }) {
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const reset = useCallback(() => setSelectedCurrency(null), []);

  const allRows = useMemo(() => buildCurrencyExposureRows(currencyBreakdown), [currencyBreakdown]);
  const rows = useMemo(
    () => (selectedCurrency ? allRows.filter((row) => row.currency === selectedCurrency) : []),
    [allRows, selectedCurrency],
  );

  const chartEvents = useMemo(() => ({
    click: (params) => {
      if (params?.componentType !== "series") return;
      setSelectedCurrency(params.name);
      onTrack?.(`Graph Slice Clicked: ${params.name}`);
    },
  }), [onTrack]);

  return { rows, canRender: allRows.length > 0, chartEvents, modalOpen: !!selectedCurrency, closeModal: reset, titleSuffix: selectedCurrency || "", reset };
}

export function CurrencyExposureBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Currency Exposure";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${rowCount} ${rowCount === 1 ? "Lease" : "Leases"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={CURRENCY_EXPOSURE_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
